import { useState } from "react";
import { Button, Form, Select, Space, Alert, Typography, message } from "antd";
import type { SelectProps } from "antd/lib/select";
import type { FormInstance } from "antd";

import NupayPaymentInfo from "../ModalForm/PaymentType/NupayPaymentInfo";
import PayAtPaymentInfo from "../ModalForm/PaymentType/Pay@PaymentInfo";
import { addCashPaymentTypeApi } from "../../services/Payment/PaymentApis";
import WizardStepLayout from "./WizardStepLayout";
import { invalidateCompliance } from "../../utils/complianceInvalidate";

const { Text } = Typography;

const paymentTypes: SelectProps["options"] = [
  {
    name: "Payment Type",
    label: "Nupay",
    value: "Nupay",
  },
  {
    name: "Payment Type",
    label: "PayAt",
    value: "PayAt",
  },
  {
    name: "Payment Type",
    label: "Cash",
    value: "Cash",
  },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 8 },
};

interface PaymentMethodStepProps {
  form: FormInstance;
  memberId: string | number;
  gotoStep: Function;
  /** Absolute wizard step for ID Document (after this payment step). */
  documentsStep: number;
}

/**
 * Create-policy payment capture: choose Nupay / PayAt / Cash.
 * Provider success (gotoStep(2) inside Nupay/PayAt) advances to documents.
 * Cash → POST /payment/cash?memberId= (PaymentType only, no mandate).
 */
const PaymentMethodStep: React.FC<PaymentMethodStepProps> = ({
  form,
  memberId,
  gotoStep,
  documentsStep,
}) => {
  const [paymentType, setPaymentType] = useState("");
  const [phase, setPhase] = useState<"choose" | "details">("choose");
  const [cashLoading, setCashLoading] = useState(false);

  const advanceToDocuments = () => {
    gotoStep(documentsStep);
  };

  /** Map Add-Payment-Method modal steps onto the parent wizard. */
  const providerGotoStep = (step: number) => {
    if (step >= 2) {
      advanceToDocuments();
      return;
    }
    if (step <= 0) {
      setPhase("choose");
      setPaymentType("");
    }
  };

  const providerSubmit = async () => true;

  const handleCashContinue = async () => {
    if (!memberId) {
      message.error("Member is required before recording Cash payment type.");
      return;
    }
    setCashLoading(true);
    try {
      const res = await addCashPaymentTypeApi(String(memberId));
      if (!res?.success) {
        message.error(res?.message || "Unable to save Cash payment type.");
        return;
      }
      message.success("Cash payment type saved.");
      invalidateCompliance();
      advanceToDocuments();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save Cash payment type."
      );
    } finally {
      setCashLoading(false);
    }
  };

  if (phase === "details" && paymentType === "Cash") {
    return (
      <WizardStepLayout
        action={
          <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                block
                size="large"
                loading={cashLoading}
                onClick={handleCashContinue}
              >
                Continue
              </Button>
              <Button
                block
                size="large"
                disabled={cashLoading}
                onClick={() => setPhase("choose")}
              >
                Back
              </Button>
            </Space>
          </Form.Item>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cash payer"
          description="No bank mandate is required. This records Cash as the payment type on the policy. You can still record cash receipts later from the member Payments tab."
        />
      </WizardStepLayout>
    );
  }

  if (phase === "details" && paymentType === "Nupay") {
    return (
      <NupayPaymentInfo
        buttonLoading={false}
        gotoStep={providerGotoStep}
        form={form}
        update={false}
        submit={providerSubmit}
        memberId={String(memberId)}
      />
    );
  }

  if (phase === "details" && paymentType === "PayAt") {
    return (
      <PayAtPaymentInfo
        buttonLoading={false}
        gotoStep={providerGotoStep}
        form={form}
        update={false}
        submit={providerSubmit}
        memberId={String(memberId)}
      />
    );
  }

  return (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            block
            size="large"
            disabled={!paymentType}
            onClick={() => {
              if (!paymentType) return;
              setPhase("details");
            }}
          >
            Next
          </Button>
        </Form.Item>
      }
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Choose how this policy will be paid.
      </Text>
      <Form.Item
        name="WizardPaymentType"
        label="Payment Type"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}
        rules={[{ required: true, message: "Select a payment type" }]}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Payment Type"
          options={paymentTypes}
          onChange={(value) => setPaymentType(value)}
        />
      </Form.Item>
    </WizardStepLayout>
  );
};

export default PaymentMethodStep;
